import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { forkJoin, map, Observable } from 'rxjs';
import { ProductLineWmsService } from './product-list-wms.service';
import { IProductsSelected, ProductListQuery } from './product-list.query';
import { ProductsWMSFilterModel } from '@kanzi-apes/kanzi-models';

@Component({
    selector: 'kanzi-apes-line-create',
    exportAs: 'lineCreate',
    templateUrl: './kanzi-ui-line-create.component.html',
    styleUrls: ['./kanzi-ui-line-create.component.scss'],
    providers: [ProductListQuery],
    standalone: false
})
export class KanziUiLineCreateComponent implements OnInit {
  private productListQuery = inject(ProductListQuery);
  private productLineWmsService = inject(ProductLineWmsService);

  // Señales de UI
  public displayDialogSkuList = signal(false);
  public displayDialogProducts = signal(false);
  public productsListText = '';
  public barCodeFilter = signal<string>('');
  public defaultImage = 'assets/images/backgrounds/not-image-400x300.png';
  filteredProductsList: IProductsSelected[] = [];

  /**
   * Resultados de búsqueda por código de barras
   */
  public readonly searchResults = signal<IProductsSelected[]>([]);

  /**
   * Indica si se está realizando una búsqueda
   */
  public readonly isSearching = signal(false);

  public filterByBarcode = computed<ProductsWMSFilterModel | null>(() => {
    const barcode = this.barCodeFilter();
    return barcode ? { barcode__contains: barcode } : null;
  });

  // Queries
  public queryProducts = this.productListQuery.productsQuery;
  public productsSelected = this.productListQuery.productsSelected;
  public listResult = this.queryProducts.data();
  public queryProductByBarcode = computed(() => {
    const result = this.productListQuery.productByBarcode(this.filterByBarcode);
    return result?.data() ?? [];
  });

  /* public filteredProducts = computed(() => {
    const queryData = this.queryProducts.data() ?? ([] as any[]);
    return queryData;
  }); */

  filteredProductsEffect = effect(() => {
    const filteredProducts = this.filteredProducts();
    if (filteredProducts.length > 0) {
      this.filteredProductsList = [...filteredProducts];
    }
  });

  public filteredProducts = computed(() => {
    const barcode = this.barCodeFilter();
    const search = this.queryProductByBarcode();
    const queryData = this.queryProducts.data() ?? [];

    console.log('Query Data:', queryData);
    console.log('Search Results:', search);
    console.log('Barcode Filter:', barcode);

    // Combinar productos
    const allProducts = [...queryData, ...search];

    // Obtener productos únicos
    const uniqueProducts = this.getUniqueProducts(allProducts);

    // Filtrar por código de barras si existe
    if (barcode) {
      return uniqueProducts.filter((product) =>
        product.barcode?.includes(barcode)
      );
    }

    return uniqueProducts;
  });

  public isProductsSelectedAmountInvalid(): boolean {
    const products = this.filteredProductsList.filter(
      (product) => product.isSelected === true
    );

    if (products.length === 0) return true;

    return products.some((product) => !product || product.amount === 0);
  }

  public get getProductsSelected(): IProductsSelected[] {
    return this.productsSelected();
  }

  public onDisplayDialogProducts(): void {
    this.displayDialogProducts.set(true);
  }

  ngOnInit(): void {
    this.productListQuery.updateFilters({ page_size: 100 });
  }

  public onSaveProducts(product: IProductsSelected[] | undefined): void {
    if (!product) return;

    const currentProducts = this.productsSelected();
    const products = [...product, ...currentProducts];

    // Obtener productos únicos y seleccionados
    const uniqueProducts = this.getUniqueProducts(products);
    const selected = uniqueProducts.filter((p) => p.isSelected);

    this.productListQuery.onSaveProductSelected(selected);
    this.displayDialogProducts.set(false);
  }

  public onDeleteRowData(rowData: IProductsSelected): void {
    // Crear una copia inmutable del producto
    const updatedProduct = { ...rowData, isSelected: false, amount: 0 };

    // Filtrar productos seleccionados
    const currentProducts = this.productsSelected();
    const newSelected = currentProducts.filter(
      (p) => p.reference_id !== updatedProduct.reference_id && p.isSelected
    );

    this.productListQuery.onSaveProductSelected(newSelected);
  }

  public saveListProducts(productsListText: string | null): void {
    if (!productsListText) return;
    const rowsProducts = productsListText
      .split('\n')
      .filter((row) => row.trim());

    if (rowsProducts.length === 0) return;

    const products = rowsProducts
      .map((item) => {
        const [reference, amount] = item.split(',');
        const referenceTrim = reference?.replace(/\s/g, '');
        const parsedAmount = Number(amount);

        if (!referenceTrim || isNaN(parsedAmount)) return null;

        return {
          amount: parsedAmount,
          reference: referenceTrim,
        };
      })
      .filter((p): p is { amount: number; reference: string } => p !== null);

    if (products.length === 0) return;

    // Agrupar por referencia y sumar cantidades
    const refSet = [...new Set(products.map((p) => p.reference))];
    const productsAmountTotal: { amount: number; reference: string }[] = [];
    const currentProducts = this.productsSelected();

    refSet.forEach((ref) => {
      const total = products
        .filter((p) => p.reference === ref)
        .reduce((sum, p) => sum + p.amount, 0);

      const existingProduct = currentProducts.find(
        (p) => p.reference_id === ref
      );

      if (existingProduct) {
        // Actualizar producto existente
        existingProduct.amount += total;
      } else {
        // Agregar nuevo producto
        productsAmountTotal.push({
          amount: total,
          reference: ref,
        });
      }
    });

    // Obtener productos nuevos
    if (productsAmountTotal.length > 0) {
      this.getProducts(productsAmountTotal).subscribe({
        next: (payload) => {
          this.productListQuery.onUpdateProductsSelected(payload);
        },
        complete: () => {
          this.onDeleteList();
        },
      });
    } else {
      this.onDeleteList();
    }
  }

  public getProducts(
    productsAmountTotal: { amount: number; reference: string }[]
  ): Observable<IProductsSelected[]> {
    const services$ = productsAmountTotal.map((p) =>
      this.productLineWmsService.getProductList({
        reference_id__eq: p.reference,
      })
    );
    return forkJoin(services$).pipe(
      map((payload) => {
        return payload.map((productComplete, index) => {
          const amount = productsAmountTotal[index].amount;
          return {
            ...productComplete.results[0],
            isSelected: true,
            amount,
          } as IProductsSelected;
        });
      })
    );
  }

  public onDeleteList() {
    this.productsListText = '';
    this.displayDialogSkuList.set(false);
  }

  /**
   * Obtiene productos únicos basándose en reference_id
   * Prioriza productos seleccionados
   */
  private getUniqueProducts(
    products: IProductsSelected[]
  ): IProductsSelected[] {
    const uniqueMap = new Map<string, IProductsSelected>();

    products.forEach((product) => {
      const refId = product.reference_id;
      if (!refId) return;

      const existing = uniqueMap.get(refId);

      // Si no existe o el actual está seleccionado y el existente no
      if (!existing || (product.isSelected && !existing.isSelected)) {
        uniqueMap.set(refId, product);
      }
    });

    return Array.from(uniqueMap.values());
  }

  /**
   * Busca productos por código de barras de forma asíncrona
   * @param barcode - Código de barras a buscar
   */
  public async onSearchByBarcode(barcode: string): Promise<void> {
    if (!barcode.trim()) {
      this.searchResults.set([]);
      return;
    }

    this.isSearching.set(true);
    try {
      const results = await this.productListQuery.searchProductByBarcode(
        barcode
      );
      this.searchResults.set(results);
    } catch (error) {
      console.error('Error searching products:', error);
      this.searchResults.set([]);
    } finally {
      this.isSearching.set(false);
    }
  }
}
